// The ground — the ONE place inside arcade where ground (x, z, h) becomes
// screen. Everything else (movement, combat, draw order, reflections, the
// authoring overlay) works in GROUND coordinates and asks this module where
// that lands. A Scene SUPPLIES the numbers through configureGround() /
// configurePerspective(); it never gets its own copy of the maths.
//
// TWO PROJECTIONS, ONE OWNER (Order 3.9984):
//
//   OBLIQUE — the belt. The TMNT street: a diagonal depth axis, linear
//   near/far render scale, x moving 1 screen px per ground px at every depth.
//   Physically a lie, historically the genre, and byte-identical to what the
//   belt scenes always did. The maths lives here because it is arcade's own.
//
//   PERSPECTIVE — the playable-cinema shot. A photographic plate calibrated
//   with a PerspectiveGround record (camera height, horizon, person gauges),
//   figures shrinking as 1/z toward a real horizon. The maths is DELEGATED to
//   @gooddesign/anim-schema's ground-math — the shared home Marionette also
//   consumes — and this module only adapts arcade's calls onto it. It holds
//   the camera FIXED per shot; nothing here may bake that assumption into the
//   shared seam (Marionette dollies the same functions per frame).
//
// COORDINATES. An actor stands at (x, z) on the ground and floats `h` above:
//   OBLIQUE     — x along the street (world px), z along the oblique depth
//                 axis (0 = near kerb .. depthPx), h in screen px.
//   PERSPECTIVE — ground-math's space: x lateral ground units (0 on the
//                 camera axis), z ground units of DEPTH FROM THE CAMERA
//                 (zNear at the plate's near seat line), h in ground units.
//                 One ground unit ≈ one plate px at the near line, the
//                 convention the courtyard's record is calibrated to.
// Screen y is an output, never anyone's input; the walkable polygon, spawns,
// exits and triggers are all authored in the active ground space.

import {
  cameraFromGround,
  pointInPolygon,
  project,
  scaleAt as unitScaleAt,
  unproject,
  type GroundCamera,
  type PerspectiveGround,
  type WalkablePolygon,
} from '@gooddesign/anim-schema/ground-math';

export type { WalkablePolygon } from '@gooddesign/anim-schema/ground-math';

/** The numbers that define one BELT location's oblique plane and how the
 *  street seats against its plate. A belt Scene assembles this (its `ground`
 *  fields plus the plate's `nearY`/`depthPx`) and hands it to
 *  configureGround() at load. */
export interface GroundDef {
  /** Depth axis: away from camera is up and to the LEFT, at this angle. */
  depthAngleDeg: number;
  /** Screen y of the ground at the near kerb (z = 0). */
  nearY: number;
  /** Depth of the walkable street, world px measured along the oblique axis. */
  depthPx: number;
  /** Playable extent along the street, world px. */
  xMin: number;
  xMax: number;
  /** Render scale at the near kerb and at the far one. TMNT would use 1 and 1. */
  nearScale: number;
  farScale: number;
}

/** What a PERSPECTIVE location hands over: the shared calibration record plus
 *  the plate seat (where the playable band sits on screen) and the coarse
 *  lateral bounds, ground units. */
export interface PerspectiveDef {
  record: PerspectiveGround;
  /** Screen y of the playable band's near edge (the plate seat, z = zNear). */
  nearY: number;
  /** The band's height on screen ABOVE nearY — the far edge sits at
   *  nearY − depthPx. Screen px; it must stay below the horizon. */
  depthPx: number;
  /** Coarse lateral clamp, ground units (the polygon does the real work). */
  xMin: number;
  xMax: number;
}

interface ObliqueActive extends GroundDef {
  mode: 'oblique';
  /** Unit vector, in screen px, from the near kerb toward the far one. */
  axis: { x: number; y: number };
}

interface PerspectiveActive {
  mode: 'perspective';
  record: PerspectiveGround;
  camera: GroundCamera;
  /** Ground depth of the near/far edges of the playable band. */
  zNear: number;
  zFar: number;
  xMin: number;
  xMax: number;
}

type ActiveGround = ObliqueActive | PerspectiveActive;

/** The px height a person-sized rig is AUTHORED at (the woman's
 *  figureHeightPx — her rig at render multiplier 1). scaleAt()'s perspective
 *  branch turns "a person is referenceHeight ground units" into a render
 *  multiplier through this basis, so a record calibrated against the plate's
 *  own people sizes the cast without per-rig knowledge leaking in here. */
const PERSON_BASIS_PX = 205;

/** The alley's seat (Order 16.4) — the default the module boots with, so
 *  anything that projects before a Scene loads still works. */
const DEFAULT_GROUND: GroundDef = {
  depthAngleDeg: 45,
  nearY: 340,
  depthPx: 150,
  xMin: 24,
  xMax: 1576,
  nearScale: 1.0,
  farScale: 0.82,
};

function deriveOblique(def: GroundDef): ObliqueActive {
  const rad = (def.depthAngleDeg * Math.PI) / 180;
  return { ...def, mode: 'oblique', axis: { x: -Math.cos(rad), y: -Math.sin(rad) } };
}

let active: ActiveGround = deriveOblique(DEFAULT_GROUND);

/** Bumped by every configure*() below. The authoring overlay re-projects its
 *  markers when this moves — a scene load or a live calibration drag changes
 *  where EVERY ground noun lands, not just the one that was edited. */
let version = 0;

export function groundVersion(): number {
  return version;
}

/** The active location's walkable polygon, or null for the plain rectangle
 *  (xMin..xMax × the z band). Installed per location alongside the plane;
 *  null keeps movement byte-identical to the pre-polygon engine. */
let walkable: WalkablePolygon | null = null;

/** Install a BELT location's oblique plane. Call once, at the top of a
 *  Scene's load, BEFORE anything projects. */
export function configureGround(def: GroundDef): void {
  active = deriveOblique(def);
  version++;
}

/** Install a PERSPECTIVE location's calibrated ground. Same contract:
 *  first thing at Scene load, before any projection. */
export function configurePerspective(def: PerspectiveDef): void {
  const camera = cameraFromGround(def.record);
  const near = unproject(camera.vanishingX, def.nearY, camera);
  const far = unproject(camera.vanishingX, def.nearY - def.depthPx, camera);
  if (!near || !far) {
    throw new Error(
      `ground: the plate seat (nearY ${def.nearY}, depthPx ${def.depthPx}) reaches the horizon (y ${camera.horizonY}) — no ground there`,
    );
  }
  active = { mode: 'perspective', record: def.record, camera, zNear: near.z, zFar: far.z, xMin: def.xMin, xMax: def.xMax };
  version++;
}

/** Install (or clear, with null) the location's walkable polygon. Call with
 *  the ground plane at Scene load — a stale polygon from the previous
 *  location would strand the player. */
export function configureWalkable(poly: WalkablePolygon | null): void {
  walkable = poly;
  version++;
}

/** The active BELT plane's numbers — the belt loader's read (spawn bounds,
 *  the road-zone overhang). A perspective scene has no oblique numbers to
 *  answer with, and a caller that wants bounds wants groundBounds(). */
export function ground(): Readonly<GroundDef> {
  if (active.mode !== 'oblique') {
    throw new Error('ground(): the active scene is a perspective shot — use groundBounds()');
  }
  return active;
}

/** The playable extent in the ACTIVE ground space, both modes. */
export function groundBounds(): { xMin: number; xMax: number; zMin: number; zMax: number } {
  return active.mode === 'oblique'
    ? { xMin: active.xMin, xMax: active.xMax, zMin: 0, zMax: active.depthPx }
    : { xMin: active.xMin, xMax: active.xMax, zMin: active.zNear, zMax: active.zFar };
}

/** The active perspective calibration, for the Shot Editor's overlay (ghost
 *  gauges size by the camera). Null on a belt scene. */
export function activePerspective(): { camera: GroundCamera; record: PerspectiveGround } | null {
  return active.mode === 'perspective' ? { camera: active.camera, record: active.record } : null;
}

/** Screen offset contributed by standing `z` deep — OBLIQUE only (in
 *  perspective the lateral drift depends on x, so no such offset exists). */
export function depthOffset(z: number): { x: number; y: number } {
  if (active.mode !== 'oblique') throw new Error('depthOffset(): perspective drift depends on x — use screenPos()');
  return { x: z * active.axis.x, y: z * active.axis.y };
}

/** Screen y of the GROUND itself at depth `z` — the surface an actor's feet
 *  rest on, and the plane its reflection mirrors about. */
export function groundScreenY(z: number): number {
  if (active.mode === 'oblique') return active.nearY + z * active.axis.y;
  return active.camera.horizonY + (active.camera.zoom * active.camera.height) / z;
}

/** Where an actor standing at (x, z), `h` above the ground, is drawn. */
export function screenPos(x: number, z: number, h: number): { x: number; y: number } {
  if (active.mode === 'oblique') {
    const o = depthOffset(z);
    return { x: x + o.x, y: active.nearY + o.y - h };
  }
  return project(x, z, active.camera, h);
}

/** The ground point under screen (sx, sy) at h = 0 — the authoring overlay's
 *  inverse (a pen click, a marker drag). Null when the point names no ground
 *  (at/above the horizon on a perspective shot). Unclamped; callers clamp. */
export function groundFromScreen(sx: number, sy: number): { x: number; z: number } | null {
  if (active.mode === 'oblique') {
    const z = (sy - active.nearY) / active.axis.y;
    return { x: sx - z * active.axis.x, z };
  }
  return unproject(sx, sy, active.camera);
}

/** Render scale at depth `z`. Simulation never calls this — only the renderer.
 *  Oblique: the authored linear near→far ramp. Perspective: the size law —
 *  a person of referenceHeight ground units at z, through the person basis. */
export function scaleAt(z: number): number {
  if (active.mode === 'oblique') {
    const t = clamp01(z / active.depthPx);
    return active.nearScale + (active.farScale - active.nearScale) * t;
  }
  return (active.record.referenceHeight * unitScaleAt(z, active.camera)) / PERSON_BASIS_PX;
}

/** A ground circle (a trigger, an exit) as the screen ellipse it reads as.
 *  Owned here so the overlay never re-derives the foreshortening: oblique
 *  squashes by the depth axis' slope; perspective squashes by H/z (the grazing
 *  angle of the ground at that depth) and scales the radius by the size law. */
export function groundCircleScreen(x: number, z: number, r: number): { cx: number; cy: number; rx: number; ry: number } {
  const c = screenPos(x, z, 0);
  if (active.mode === 'oblique') {
    return { cx: c.x, cy: c.y, rx: r, ry: r * Math.abs(active.axis.y) };
  }
  const rx = r * unitScaleAt(z, active.camera);
  return { cx: c.x, cy: c.y, rx, ry: rx * Math.min(1, active.camera.height / z) };
}

/** Draw order: nearer actors occlude further ones. Normalized over the active
 *  z band; each actor owns a band 1.0 wide (its parts stack inside it). */
const ACTOR_DEPTH_BASE = 10;
const ACTOR_DEPTH_SPAN = 20;
export function actorDepth(z: number): number {
  return ACTOR_DEPTH_BASE + (1 - depthT(z)) * ACTOR_DEPTH_SPAN;
}

/** Draw order for an actor's ground reflection — a band strictly BELOW every
 *  actor body (see Order 16.4). */
const REFLECTION_DEPTH_BASE = 4;
const REFLECTION_DEPTH_SPAN = 4;
export function reflectionDepth(z: number): number {
  return REFLECTION_DEPTH_BASE + (1 - depthT(z)) * REFLECTION_DEPTH_SPAN;
}

function depthT(z: number): number {
  const b = groundBounds();
  return clamp01((z - b.zMin) / (b.zMax - b.zMin));
}

export const clampX = (x: number) => {
  const b = groundBounds();
  return Math.max(b.xMin, Math.min(b.xMax, x));
};
export const clampZ = (z: number) => {
  const b = groundBounds();
  return Math.max(b.zMin, Math.min(b.zMax, z));
};
const clamp01 = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t);

/** Clamp a movement step (from → to) against the walkable ground.
 *
 *  With no polygon installed this is EXACTLY the rectangle clamp — the belt
 *  scenes play byte-identically. With one, the step is tried whole, then per
 *  axis (which is what lets an actor slide along a wall it walked into), then
 *  refused. The point-in-polygon itself — holes and all — is ground-math's;
 *  this module only owns the slide. `from` is trusted to be walkable; if
 *  authoring ever spawns an actor outside the polygon it stays put, loudly
 *  visible, rather than teleporting. */
export function clampWalk(
  fromX: number,
  fromZ: number,
  toX: number,
  toZ: number,
): { x: number; z: number } {
  const x = clampX(toX);
  const z = clampZ(toZ);
  if (!walkable) return { x, z };
  if (pointInPolygon(x, z, walkable)) return { x, z };
  if (pointInPolygon(x, fromZ, walkable)) return { x, z: fromZ };
  if (pointInPolygon(fromX, z, walkable)) return { x: fromX, z };
  return { x: fromX, z: fromZ };
}
